<script>
import Input from '@/components/Utils/Input.vue'
import FloatingMenu from '@/composables/floatingMenu'
import NewColorPicker from '@/components/Utils/NewColorPicker.vue'
import { VSelect } from 'vuetify/lib/components'
import SettingModel from '@/components/OptimizeSite/settingModel.vue'
import { apiGetFloatingMenuInfo, apiCreateFloatingMenu, apiEditFloatingMenu, apiGetDemoImg } from '@/api/floating_menu'

export default {
  name: 'FloatingMenuSetting',
  components: { SettingModel, Input, NewColorPicker, VSelect },
  data() {
    return {
      dialog: false,
      floating_menu_pc: null,
      floating_menu_mobile: null,
      demo_background_img: {},
      // 因為目前一個容器只會有一個浮球, 暫時先這樣記 id, 之後如果有要建立多個浮球, 會再開一個浮球清單頁, 選擇要編輯的浮球才會進到這一頁, 點擊的時候再把 id 帶到 path
      floating_menu_id: null,
      form_data: {},
      preview_url: '',
      form_items_name: {
        position: '浮球定位',
        btn_size: '浮球大小',
        menu_btn_background: '主選單顏色',
        to_top_btn: '置頂按鈕設定',
        status: '啟動',
        background: '浮球背景色',
        img_url: '圖片網址',
        link: '連結網址',
      },
      release_status: 'draft',
      release_status_operation: {
        draft: () => this.releaseStatusSwitch(true),
        release: () => this.releaseStatusSwitch(false),
        end: () => {},
      },
      is_edit_form: false,
      first_loading_finish: false,
      save_btn_color: 'primary',
      stepper_show_page: 1,
    }
  },
  computed: {
    form_data_setting() {
      const setting_data = {}
      for (const key in this.form_data) {
        if (key !== 'custom_floats') {
          setting_data[key] = this.form_data[key]
        }
      }
      return setting_data
    },
  },
  watch: {
    form_data: {
      handler() {
        if (this.first_loading_finish) {
          this.is_edit_form = true
          this.save_btn_color = 'warning'
        }
      },
      deep: true,
    },
  },
  async mounted() {
    this.floating_menu_pc = new FloatingMenu()
    this.floating_menu_mobile = new FloatingMenu()
    await this.floating_menu_pc.createFloatingMenu(document.querySelector('#demo_pc'))
    await this.floating_menu_mobile.createFloatingMenu(document.querySelector('#demo_mobile'))
    document.querySelector('#demo_pc iframe').setAttribute('data-id', 'floating_menu_pc')
    document.querySelector('#demo_mobile iframe').setAttribute('data-id', 'floating_menu_mobile')
    this.floating_menu_pc.changeContainerPositionMode('absolute')
    this.floating_menu_mobile.changeContainerPositionMode('absolute')
    const floating_menu_config = await this.getFloatingMenuData()
    // 更新 Form data
    const curr_data = this.floatingMenuSettingFormat(floating_menu_config)
    curr_data.custom_floats = []
    floating_menu_config.custom_floats.forEach((item) => {
      curr_data.custom_floats.push(this.customFloatsFormat(item))
    })
    this.form_data = curr_data
    // 執行操作
    for (const [key, item] of Object.entries(this.form_data_setting)) {
      if (['to_top_btn', 'btn_size'].includes(key)) {
        for (const to_top_btn_item of Object.values(item)) {
          await this.editFloatingMenu({ $event: to_top_btn_item, item: to_top_btn_item })
        }
      } else {
        await this.editFloatingMenu({ $event: item, item })
      }
    }

    for (const float of this.form_data.custom_floats) {
      this.floating_menu_pc.addFloat()
      this.floating_menu_mobile.addFloat()
      for (const [key, item] of Object.entries(float)) {
        if (key === 'id') continue
        await this.editFloatingMenu({ $event: item, item, id: float.id })
      }
    }
    this.first_loading_finish = true

    await this.getDemoImg()
  },
  methods: {
    // API
    async getFloatingMenuData() {
      try {
        let res = await apiGetFloatingMenuInfo(this.$route.params.org_id, this.$route.params.container_id)
        // 因為現在一個容器只會有一個浮球, 預設是 0 個, 所以暫時在這偷建, 之後有建立多個浮球的需求再拉到外面
        if (!res) {
          const floating_menu_config = JSON.stringify(this.floating_menu_pc.getFloatingMenuInfo())
          const create_res = await apiCreateFloatingMenu(
            this.$route.params.org_id,
            this.$route.params.container_id,
            floating_menu_config
          )
          if (create_res) {
            res = await apiGetFloatingMenuInfo(this.$route.params.org_id, this.$route.params.container_id)
          } else {
            throw new Error('新增失敗')
          }
        }
        this.floating_menu_id = res.id
        this.preview_url = res.preview_url
        this.release_status = res.activate ? 'release' : 'draft'
        if (res.add_column) {
          return JSON.parse(res.add_column)
        }
        throw new Error('取得資料失敗')
      } catch (error) {
        console.error('getFloatingMenuData() :', error)
        this.$root.vtoast.show({ message: '取得資料失敗', color: 'error' })
        // 這是預設值
        return this.floating_menu_pc.getFloatingMenuInfo()
      }
    },
    async getDemoImg() {
      try {
        const res = await apiGetDemoImg(this.$route.params.org_id, this.$route.params.container_id)
        this.demo_background_img = { pc: res.pc_url, mobile: res.phone_url }
      } catch (error) {
        console.error('getDemoImg() :', error)
      }
    },
    // 動態元件
    dynamicComponentName(data) {
      const components = {
        input: Input,
        color_picker: NewColorPicker,
        selector: VSelect,
      }
      return components[data.component_name]
    },
    dynamicProps(data) {
      const components_props = {
        input: {
          type: data.type,
          label: data.label,
          value: data.value,
          spinButtons: data.spin_buttons || false,
          rules: data.rules ?? [],
        },
        color_picker: {
          'props-input-value': data.value,
        },
        selector: {
          value: data.value,
          items: data.options,
          outlined: true,
          dense: true,
          'return-object': true,
          hideDetails: 'auto',
        },
      }
      return components_props[data.component_name]
    },
    dynamicEvent(data, id) {
      const components_event = {
        input: {
          'update-input': async (val) => await this.editFloatingMenu({ $event: val, item: data, id }),
        },
        color_picker: {
          'update-color': async (val) => await this.editFloatingMenu({ $event: val, item: data, id }),
        },
        selector: {
          input: async (val) => await this.editFloatingMenu({ $event: val, item: data, id }),
        },
      }
      return components_event[data.component_name]
    },
    // 整理 form format
    floatingMenuSettingFormat(setting_data) {
      return {
        btn_size: {
          pc: {
            component_name: 'input',
            operate: 'btn_size_pc',
            type: 'number',
            label: '桌機',
            spin_buttons: true,
            value: setting_data.btn_size_pc,
          },
          mobile: {
            component_name: 'input',
            operate: 'btn_size_mobile',
            type: 'number',
            label: '手機',
            spin_buttons: true,
            value: setting_data.btn_size_mobile,
          },
        },
        position: {
          component_name: 'selector',
          options: [
            {
              text: '右上',
              value: 'top-right',
            },
            {
              text: '右下',
              value: 'bottom-right',
            },
            {
              text: '左上',
              value: 'top-left',
            },
            {
              text: '左下',
              value: 'bottom-left',
            },
          ],
          operate: 'position',
          value: setting_data.position,
        },
        menu_btn_background: {
          component_name: 'color_picker',
          operate: 'menu_btn_background',
          value: setting_data.menu_btn_background,
        },
        to_top_btn: {
          status: {
            component_name: 'selector',
            options: [
              {
                text: '關',
                value: false,
              },
              {
                text: '開',
                value: true,
              },
            ],
            operate: 'to_top_btn_status',
            value: setting_data.to_top_btn.status,
          },
          background: {
            component_name: 'color_picker',
            operate: 'to_top_btn_background',
            value: setting_data.to_top_btn.background,
          },
        },
      }
    },
    customFloatsFormat(custom_float_data) {
      return {
        id: custom_float_data.id,
        status: {
          component_name: 'selector',
          options: [
            {
              text: '關',
              value: false,
            },
            {
              text: '開',
              value: true,
            },
          ],
          operate: 'custom_float_status',
          value: custom_float_data.status,
        },
        background: {
          component_name: 'color_picker',
          operate: 'custom_float_background',
          value: custom_float_data.background,
        },
        img_url: {
          component_name: 'input',
          type: 'text',
          operate: 'custom_float_img_url',
          value: custom_float_data.img_url,
          rules: [this.checkUrlHttps, this.checkImgUrlFormat],
        },
        link: {
          component_name: 'input',
          type: 'text',
          operate: 'custom_float_link',
          value: custom_float_data.link,
          rules: [this.checkUrlHttps],
        },
      }
    },
    // 操作浮球元件
    updateFormDataCustomFloats() {
      const curr_custom_floats = []
      const update_data = this.floating_menu_pc.getFloatingMenuInfo()
      update_data.custom_floats.forEach((item) => {
        curr_custom_floats.push(this.customFloatsFormat(item))
      })
      this.form_data.custom_floats = curr_custom_floats
    },
    async editFloatingMenu({ $event, item, id }) {
      // 執行操作
      const operate_types = {
        btn_size_pc: (val) => this.floating_menu_pc.changeBtnSize(val),
        btn_size_mobile: (val) => this.floating_menu_mobile.changeBtnSize(val),
        position: (val) => {
          this.floating_menu_pc.changeFloatPosition(val)
          this.floating_menu_mobile.changeFloatPosition(val)
        },
        menu_btn_background: (val) => {
          this.floating_menu_pc.changeMenuBtnBackground(val)
          this.floating_menu_mobile.changeMenuBtnBackground(val)
        },
        to_top_btn_status: (val) => {
          this.floating_menu_pc.settingToTopFloat({ status: val })
          this.floating_menu_mobile.settingToTopFloat({ status: val })
        },
        to_top_btn_background: (val) => {
          this.floating_menu_pc.settingToTopFloat({ background: val })
          this.floating_menu_mobile.settingToTopFloat({ background: val })
        },
        custom_float_status: (val) => {
          this.floating_menu_pc.editFloat({ id, status: val })
          this.floating_menu_mobile.editFloat({ id, status: val })
        },
        custom_float_background: (val) => {
          this.floating_menu_pc.editFloat({ id, background: val })
          this.floating_menu_mobile.editFloat({ id, background: val })
        },
        custom_float_img_url: (val) => {
          this.floating_menu_pc.editFloat({ id, img_url: val })
          this.floating_menu_mobile.editFloat({ id, img_url: val })
        },
        custom_float_link: (val) => {
          this.floating_menu_pc.editFloat({ id, link: val })
          this.floating_menu_mobile.editFloat({ id, link: val })
        },
      }
      await operate_types[item.operate]($event.value)
      // 更新 Form data
      item.value = $event.value
    },
    addFloat() {
      // 操作 Floating menu
      this.floating_menu_pc.addFloat()
      this.floating_menu_mobile.addFloat()
      // 更新 Form data
      this.updateFormDataCustomFloats()
    },
    deleteFloat(id) {
      // 操作 Floating menu
      this.floating_menu_pc.deleteFloat(id)
      this.floating_menu_mobile.deleteFloat(id)
      // 更新 Form data
      this.updateFormDataCustomFloats()
    },
    async save() {
      try {
        if (!this.$refs.form_setting.validate()) {
          this.stepper_show_page = 1
          throw new Error('表單格式錯誤')
        }
        if (!this.$refs.form_custom.validate()) {
          this.stepper_show_page = 2
          throw new Error('表單格式錯誤')
        }

        const curr_floating_menu_config = this.floating_menu_pc.getFloatingMenuInfo()
        const floating_menu_config = {
          btn_size_pc: curr_floating_menu_config.btn_size,
          btn_size_mobile: this.floating_menu_mobile.getFloatingMenuInfo().btn_size,
          position: curr_floating_menu_config.position,
          menu_btn_background: curr_floating_menu_config.menu_btn_background,
          to_top_btn: curr_floating_menu_config.to_top_btn,
          custom_floats: curr_floating_menu_config.custom_floats,
        }

        const config = {
          id: this.floating_menu_id,
          container_id: this.$route.params.container_id,
          add_column: JSON.stringify(floating_menu_config),
        }

        const res = await apiEditFloatingMenu(this.$route.params.org_id, config)
        if (res) {
          this.is_edit_form = false
          this.save_btn_color = 'primary'
          return this.$root.vtoast.show({ message: '保存成功', color: 'primary' })
        }
        throw new Error('表單儲存失敗')
      } catch (error) {
        console.log('save() :', error)
        this.$root.vtoast.show({ message: error.code ? '表單儲存失敗' : error.message, color: 'error' })
      }
    },
    preview() {
      if (this.is_edit_form) {
        return this.$root.vtoast.show({ message: '請先保存表單即可預覽', color: 'warning' })
      }
      this.dialog = true
    },
    async previewMode(mode) {
      if (!this.$refs.form_preview.validate()) {
        return this.$root.vtoast.show({ message: '預覽網址錯誤', color: 'error' })
      }

      const config = {
        id: this.floating_menu_id,
        container_id: this.$route.params.container_id,
        preview_url: this.preview_url,
      }
      await apiEditFloatingMenu(this.$route.params.org_id, config)

      window.open(
        `${this.preview_url}?mode=preview`,
        '',
        mode === 'mobile' ? `width=385px,height=800px,left=${window.screen.width - 650}px,top=50px` : ''
      )
    },
    checkUrlHttps(url) {
      const regx = new RegExp('https://')
      return regx.test(url) || '格式錯誤'
    },
    checkImgUrlFormat(url) {
      const regx = new RegExp('.jpg|.jpeg|.png')
      return regx.test(url) || '格式錯誤，僅支援: .jpg/.jpeg/.png'
    },
    async releaseStatusSwitch(status) {
      const config = {
        id: this.floating_menu_id,
        activate: status,
        container_id: this.$route.params.container_id,
      }
      const res = await apiEditFloatingMenu(this.$route.params.org_id, config)
      if (res) {
        this.release_status = status ? 'release' : 'draft'
      }
    },
  },
}
</script>

<template>
  <div>
    <setting-model
      hidden-delete-btn
      :start-at="1747187890182"
      :end-at="1747670400000"
      :release-status="release_status"
      :stepper="['基本設定', '自訂浮球']"
      :save-operation="save"
      :save-operation-btn-color="save_btn_color"
      :preview-operation="preview"
      :release-status-operation="release_status_operation"
      :stepper-show-page="stepper_show_page"
      @update-stepper_show_page="(val) => (stepper_show_page = val)"
    >
      <template #基本設定>
        <v-form ref="form_setting" class="d-flex flex-column gap-3 pa-3" @submit.prevent>
          <template v-for="(setting_value, setting_title, setting_index) in form_data_setting">
            <div :key="`setting_${setting_index}`">
              <p class="mb-2">{{ form_items_name[setting_title] }}</p>
              <template v-if="['to_top_btn', 'btn_size'].includes(setting_title)">
                <div class="d-flex flex-column gap-3">
                  <template v-for="(to_top_btn_option_value, to_top_btn_option) in setting_value">
                    <component
                      :is="dynamicComponentName(to_top_btn_option_value)"
                      :key="to_top_btn_option"
                      v-bind="dynamicProps(to_top_btn_option_value)"
                      v-on="dynamicEvent(to_top_btn_option_value)"
                    />
                  </template>
                </div>
              </template>
              <template v-else>
                <component
                  :is="dynamicComponentName(setting_value)"
                  v-bind="dynamicProps(setting_value)"
                  v-on="dynamicEvent(setting_value)"
                />
              </template>
            </div>
          </template>
        </v-form>
      </template>

      <template #自訂浮球>
        <v-form ref="form_custom" class="pa-3 overflow-y-auto" style="height: 60vh" @submit.prevent>
          <div class="d-flex justify-end align-center">
            <v-btn
              color="primary"
              :to="`/${$route.params.org_id}/${$route.params.container_id}/toolbox/image_gallery`"
              v-text="'前往上傳圖片'"
            />
          </div>

          <v-divider class="my-3" />

          <div
            v-for="(float, float_index) in form_data.custom_floats"
            :key="`custom_float_${float_index}`"
            class="d-flex flex-column gap-3"
          >
            <v-divider v-if="float_index" class="mt-3" />

            <div class="d-flex justify-space-between align-center">
              <p class="ma-0">{{ `自訂${float_index + 1}` }}</p>
              <v-btn icon text @click="deleteFloat(float.id)">
                <v-icon v-text="'mdi-close'" />
              </v-btn>
            </div>
            <template v-for="(option_value, option_key, option_index) in float">
              <div v-if="option_key !== 'id'" :key="`custom_float_option_${option_index}`" class="px-1">
                <p class="mb-2">{{ form_items_name[option_key] }}</p>
                <component
                  :is="dynamicComponentName(option_value)"
                  v-bind="dynamicProps(option_value)"
                  v-on="dynamicEvent(option_value, float.id)"
                />
              </div>
            </template>
          </div>
          <v-btn class="mt-3" color="primary" @click="addFloat()" v-text="'新增浮球'" />
        </v-form>
      </template>

      <template #demo>
        <v-card
          id="demo_mobile"
          class="d-flex justify-center align-center demo-background"
          style="margin: 0 auto 12px auto"
          :style="`background-image: url(${demo_background_img.mobile});`"
          min-width="400"
          max-width="300"
          height="500"
        >
          <v-icon v-if="!demo_background_img.mobile" x-large color="black" v-text="'mdi-cellphone'" />
        </v-card>

        <v-card
          id="demo_pc"
          class="d-flex justify-center align-center demo-background"
          style="margin: 0 auto"
          :style="`background-image: url(${demo_background_img.pc});`"
          min-width="400"
          max-width="600"
          height="400"
        >
          <v-icon v-if="!demo_background_img.pc" x-large color="black" v-text="'mdi-laptop'" />
        </v-card>
      </template>
    </setting-model>

    <v-dialog v-model="dialog" max-width="600">
      <v-card>
        <v-card-title class="primary d-flex justify-space-between align-center">
          <p class="ma-0 white--text" v-text="'預覽'" />
          <v-btn icon text @click="dialog = false">
            <v-icon color="white" v-text="'mdi-close'" />
          </v-btn>
        </v-card-title>
        <v-form ref="form_preview" class="d-flex flex-column pa-4" style="gap: 12px" @submit.prevent>
          <div>
            <p class="mb-2" v-text="'請輸入預覽網址'" />
            <Input :value="preview_url" :rules="[checkUrlHttps]" @update-input="(val) => (preview_url = val.value)" />
          </div>
          <div>
            <p class="mb-2" v-text="'請選擇尺寸'" />
            <div class="d-flex justify-space-around">
              <v-btn color="primary" @click="previewMode('pc')">
                <v-icon v-text="'mdi-monitor'" />
                桌機版
              </v-btn>
              <v-btn color="primary" @click="previewMode('mobile')">
                <v-icon v-text="'mdi-cellphone'" />
                手機版
              </v-btn>
            </div>
          </div>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.gap-3 {
  gap: 12px;
}
.demo-background {
  background-size: cover;
  background-position: center top;
}
</style>
